import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { LocaleProvider } from "@/contexts/LocaleContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AuthCallback } from "@/components/auth/AuthCallback";
import { Component, lazy, Suspense, type ErrorInfo, type ReactNode } from "react";

// ---------------------------------------------------------------------------
// Error Boundary — shows the crash instead of a black screen
// ---------------------------------------------------------------------------
class AppErrorBoundary extends Component<
  { children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null };

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("[Kursplan] Render crash:", error, info.componentStack);
  }

  render() {
    if (this.state.error) {
      const isStaleChunk = /Failed to fetch dynamically imported module|Importing a module script failed/i.test(
        this.state.error.message,
      );

      return (
        <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0f0a14", color: "#f5f0e8", fontFamily: "'DM Sans', sans-serif", padding: "2rem" }}>
          <div style={{ maxWidth: "32rem", textAlign: "center" }}>
            <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: "2rem", marginBottom: "1rem" }}>
              {isStaleChunk ? "Neue Version verfügbar" : "Etwas ist schiefgelaufen"}
            </h1>
            <p style={{ opacity: 0.7, marginBottom: "1.5rem" }}>
              {isStaleChunk
                ? "Die Seite wurde zwischenzeitlich aktualisiert. Bitte neu laden, um fortzufahren."
                : "Beim Starten ist ein Fehler aufgetreten."}
            </p>
            {!isStaleChunk && (
              <pre style={{ textAlign: "left", background: "rgba(255,255,255,0.05)", padding: "1rem", borderRadius: "0.5rem", fontSize: "0.75rem", overflow: "auto", maxHeight: "12rem", whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
                {this.state.error.message}
                {"\n\n"}
                {this.state.error.stack}
              </pre>
            )}
            <button onClick={() => window.location.reload()} style={{ marginTop: "1.5rem", padding: "0.5rem 1.5rem", background: "#4fd1c5", color: "#0f0a14", border: "none", borderRadius: "0.375rem", cursor: "pointer", fontWeight: 600 }}>
              Neu laden
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

const Home = lazy(() => import("./pages/Home"));
const StudioStorefront = lazy(() => import("./pages/StudioStorefront"));
const Schedule = lazy(() => import("./pages/Schedule"));
const MySchedule = lazy(() => import("./pages/MySchedule"));
const GuestBookings = lazy(() => import("./pages/GuestBookings"));
const Account = lazy(() => import("./pages/Account"));
const Login = lazy(() => import("./pages/auth/Login"));
const Register = lazy(() => import("./pages/auth/Register"));
const Reset = lazy(() => import("./pages/auth/Reset"));
const ResetConfirm = lazy(() => import("./pages/auth/ResetConfirm"));
const NotFound = lazy(() => import("./pages/NotFound"));
const Legal = lazy(() => import("./pages/Legal"));

const ManageDashboard = lazy(() => import("./pages/manage/Dashboard"));
const ScheduleManage = lazy(() => import("./pages/manage/ScheduleManage"));
const StudentsManage = lazy(() => import("./pages/manage/Students"));
const TeachersManage = lazy(() => import("./pages/manage/Teachers"));
const OfferingsManage = lazy(() => import("./pages/manage/Offerings"));
const SettingsManage = lazy(() => import("./pages/manage/Settings"));
const OnboardingManage = lazy(() => import("./pages/manage/Onboarding"));
const MemberDetailManage = lazy(() => import("./pages/manage/MemberDetail"));

const GroupBooking = lazy(() => import("./pages/GroupBooking"));

const EmbedSchedule = lazy(() => import("./pages/embed/EmbedSchedule"));

const queryClient = new QueryClient();

const RouteLoadingFallback = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
  </div>
);

const App = () => (
  <AppErrorBoundary>
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <LocaleProvider>
        <ThemeProvider>
          <AuthProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <Suspense fallback={<RouteLoadingFallback />}>
                <Routes>
                  {/* ---- Root: sends the visitor to the right place —
                       staff to /manage, members to the schedule, guests to login
                       (see pages/Home.tsx). ---- */}
                  <Route path="/" element={<Home />} />

                  {/* ---- Public studio storefront (slug-driven; what per-studio
                       subdomains will render). Gated on studios.discoverable. ---- */}
                  <Route path="/s/:slug" element={<StudioStorefront />} />

                  {/* ---- Student-facing routes ---- */}
                  <Route path="/schedule" element={<Schedule />} />

                  {/* ---- Nutzungs-/Teilnahmebedingungen (bei Registrierung zugestimmt) ---- */}
                  <Route path="/terms" element={<Legal />} />
                  <Route path="/waiver" element={<Legal />} />

                  {/* ---- Auth routes ---- */}
                  <Route path="/auth/login" element={<Login />} />
                  <Route path="/auth/register" element={<Register />} />
                  <Route path="/auth/reset" element={<Reset />} />
                  <Route path="/auth/reset-confirm" element={<ResetConfirm />} />
                  <Route path="/auth/callback" element={<AuthCallback />} />

                  {/* ---- Private group booking (no login) ---- */}
                  <Route path="/g/:token" element={<GroupBooking />} />

                  {/* ---- Authenticated member routes ---- */}
                  <Route path="/my-schedule" element={<ProtectedRoute permission="member.view_profile"><MySchedule /></ProtectedRoute>} />
                  {/* Kein Konto nötig: eigene Buchung per Telefonnummer nachschlagen/stornieren. */}
                  <Route path="/meine-buchungen" element={<GuestBookings />} />
                  <Route path="/account" element={<ProtectedRoute permission="member.view_profile"><Account /></ProtectedRoute>} />

                  {/* ---- Studio management routes (/manage) ----
                       Guards: "studio.manage_schedule" = owner + admin (day-to-day mgmt);
                               "studio.manage_settings"  = owner only (settings). */}
                  <Route path="/manage" element={<ProtectedRoute permission="studio.manage_schedule" unauthorizedRedirect="/manage/onboarding"><ManageDashboard /></ProtectedRoute>} />
                  <Route path="/manage/schedule" element={<ProtectedRoute permission="studio.manage_schedule" unauthorizedRedirect="/manage/onboarding"><ScheduleManage /></ProtectedRoute>} />
                  <Route path="/manage/students" element={<ProtectedRoute permission="studio.manage_schedule" unauthorizedRedirect="/manage/onboarding"><StudentsManage /></ProtectedRoute>} />
                  <Route path="/manage/teachers" element={<ProtectedRoute permission="studio.manage_schedule" unauthorizedRedirect="/manage/onboarding"><TeachersManage /></ProtectedRoute>} />
                  <Route path="/manage/offerings" element={<ProtectedRoute permission="studio.manage_schedule" unauthorizedRedirect="/manage/onboarding"><OfferingsManage /></ProtectedRoute>} />
                  <Route path="/manage/settings" element={<ProtectedRoute permission="studio.manage_settings" unauthorizedRedirect="/manage/onboarding"><SettingsManage /></ProtectedRoute>} />
                  {/* No permission gate: this is where a new owner *creates* their
                       studio, before they hold any studio role. Authenticated-only;
                       the onboarding edge function authorizes each write. */}
                  <Route path="/manage/onboarding" element={<ProtectedRoute><OnboardingManage /></ProtectedRoute>} />
                  <Route path="/manage/members/:id" element={<ProtectedRoute permission="studio.manage_schedule"><MemberDetailManage /></ProtectedRoute>} />

                  {/* ---- Embeddable widget (chrome-less, public) ---- */}
                  <Route path="/embed/schedule/:slug" element={<EmbedSchedule />} />

                  {/* ---- Catch-all ---- */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
                </Suspense>
              </BrowserRouter>
            </TooltipProvider>
          </AuthProvider>
        </ThemeProvider>
      </LocaleProvider>
    </QueryClientProvider>
  </HelmetProvider>
  </AppErrorBoundary>
);

export default App;
